import React, {useState, useEffect, useContext, useRef} from 'react';
import axios from "@common/axios";
import {Divider, Dropdown, Form, Input, List, Select} from "antd";
import CircularLoader from "@common/CircularLoader";
import GenericDialog from "@common/GenericDialog";
import {Delete, MoreHorizRounded} from "@mui/icons-material";
import useCurrentConcept from "@hooks/useCurrentConcept";
import {UserContext} from "@context/UserContext";
import Button from "@common/Button";
import ButtonsGroup from "@common/ButtonsGroup";
import PopConfirm from "@common/PopConfirm";
import FolderOutlineIcon from "@common/icons/FolderOutlineIcon";
import Tooltip from "@common/Tooltip";
import {useScreenMode} from "@common/context/ScreenModeContext";

const ManageBookmarks = props => {
    const {mobileScreenMode} = useScreenMode();

    const {loadCurrentConcept} = useCurrentConcept();
    const {user} = useContext(UserContext);

    const [isFolderLoading, setFolderLoading] = useState(true);
    const [isBookmarkLoading, setBookmarkLoading] = useState(true);

    const [addFolderForm] = Form.useForm();
    const [editFolderForm] = Form.useForm();
    const [editBookmarkForm] = Form.useForm();

    const renameGroupBtnRef = useRef();

    const [folders, setFolders] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedFolderKey, setSelectedFolderKey] = useState(null);
    const [selectedBookmark, setSelectedBookmark] = useState(null);

    const [isAddFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
    const [isEditFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
    const [isEditBookmarkDialogOpen, setEditBookmarkDialogOpen] = useState(false);

    useEffect(() => {
        getUserLinkGroups();

        window.addEventListener('bookmarkUpdated', getUserLinkGroups);
        return () => {
            window.removeEventListener('bookmarkUpdated', getUserLinkGroups);
        }
    }, []);

    useEffect(() => {
        if (folders !== null && folders.length > 0 && selectedFolder === null) {
            handleSelectFolder(folders[0]);
        }
    }, [folders]);

    const getUserLinkGroups = () => {
        setFolderLoading(true);
        axios.get('/web/ui/linking/findUserLinkGroups')
            .then(response => {
                setFolders(response.data);
            }).finally(() => {
            setFolderLoading(false);
            setBookmarkLoading(false);
        });
    }

    const onAddLinkGroup = async () => {
        const linkGroupName = addFolderForm.getFieldValue('name');

        if (linkGroupName === '' || linkGroupName === undefined) {
            addFolderForm.validateFields();
            displayToastMessage('Bookmark folder name is invalid', null, 'error', undefined, 3000);
            return Promise.reject(new Error('Bookmark folder name is invalid'));
        }

        await axios.post('/web/ui/linking/createLinkGroup', null, {params: {name: linkGroupName}})
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                displayToastMessage('Bookmark folder created successfully', '', 'success', undefined, 3000);
                addFolderForm.resetFields();
                window.dispatchEvent(new Event('bookmarkUpdated'));
            });
    }

    const onEditLinkGroup = async () => {
        const linkGroupName = editFolderForm.getFieldValue('name');

        if (linkGroupName === '' || linkGroupName === undefined) {
            editFolderForm.validateFields();
            displayToastMessage('Bookmark folder name is invalid', null, 'error', undefined, 3000);
            return Promise.reject(new Error('Bookmark folder name is invalid'));
        }

        await axios.post('/web/ui/linking/editLinkGroup', null, {
            params: {
                linkGroupId: selectedFolder.linkGroupId,
                name: linkGroupName
            }
        })
            .then(response => {
                handleSelectFolder(response.data);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setEditFolderDialogOpen(false);
                window.dispatchEvent(new Event('bookmarkUpdated'));
            });
    }

    const onArchiveLinkGroup = async () => {
        handleSelectFolder(null);
        setFolderLoading(true);
        setBookmarkLoading(true);
        await axios.post('/web/ui/linking/archiveLinkGroup', null, {params: {linkGroupId: selectedFolder.linkGroupId}})
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                window.dispatchEvent(new Event('bookmarkUpdated'));
            });
    }

    const onEditLink = async () => {
        const linkName = editBookmarkForm.getFieldValue('name');

        if (linkName === '' || linkName === undefined) {
            editBookmarkForm.validateFields();
            displayToastMessage('Bookmark name is invalid', null, 'error', undefined, 3000);
            return Promise.reject(new Error('Bookmark name is invalid'));
        }

        if (selectedBookmark === null) {
            return Promise.reject(new Error('Bookmark is invalid'));
        }

        setBookmarkLoading(true);

        await axios.post('/web/ui/linking/editLink', null, {
            params: {
                linkUrlId: selectedBookmark.linkUrlId,
                linkGroupId: selectedFolder.linkGroupId,
                name: linkName
            }
        })
            .then(response => {
                handleSelectFolder(response.data);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setSelectedBookmark(null);
                setEditBookmarkDialogOpen(false);
                window.dispatchEvent(new Event('bookmarkUpdated'));
            });
    }

    const onArchiveLink = async (linkUrlId) => {
        setBookmarkLoading(true);

        await axios.post('/web/ui/linking/archiveLink', null, {
            params: {
                linkUrlId: linkUrlId,
                linkGroupId: selectedFolder.linkGroupId
            }
        })
            .then(response => {
                handleSelectFolder(response.data);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                window.dispatchEvent(new Event('bookmarkUpdated'));
            });
    }

    const handleSelectFolder = (folder) => {
        setSelectedFolder(folder);
        setSelectedFolderKey(folder != null ? folder.linkGroupId : null);

        renameGroupBtnRef.current?.focus();
    }

    const followBookMarkLink = (e) => {
        if (e.type == 'click' || (e.type == 'keydown' && (e.code == 'Enter' || e.code == 'Space'))) {
            const data = e.target.dataset;
            if (data.syllabusId === user.syllabusId) {
                loadCurrentConcept(data.conceptId, data.urlHash);
            } else {
                window.location.href = data.url;
            }
        }
    }

    return (
        <>
            <div className={'ch-manage-bookmarks-wrapper'}>
                <div className={'ch-manage-bookmarks-heading'}>
                    {!mobileScreenMode &&
                        <h2 className={'padding--m'}>Bookmark folders</h2>
                    }
                    <CircularLoader visible={isFolderLoading} pageCentered={false} displayInElement={true}
                                    spacing={true}/>
                </div>
                {folders !== null && folders.length === 0 &&
                    <div className={'ch-manage-bookmarks-folders'}>
                        <Button
                            onClick={() => {
                                setAddFolderDialogOpen(true);
                            }}
                            aria-label={'Add bookmark folder'}
                            type={'outline'}
                            style={{width: '90%', margin: '0 auto'}}
                        >
                            Add folder
                        </Button>
                        <Divider style={{margin: '8px 0'}}/>
                        <p className="ch-warning-text padding--l">Click 'Add' to create a bookmark folder</p>
                    </div>
                }
                {selectedFolder !== null &&
                    <>
                        <div className={'ch-manage-bookmarks-folders'}>
                            <CircularLoader visible={isFolderLoading} pageCentered={false} displayInElement={true}
                                            spacing={true}/>
                            {!isFolderLoading &&
                                <>
                                    {!mobileScreenMode &&
                                        <>
                                            <Button
                                                onClick={() => {
                                                    setAddFolderDialogOpen(true);
                                                }}
                                                aria-label={'Add bookmark folder'}
                                                type={'outline'}
                                                style={{width: '90%', margin: '0 auto'}}
                                            >
                                                Add folder
                                            </Button>
                                            <Divider style={{margin: '8px 0'}}/>
                                            <List
                                                dataSource={folders}
                                                locale={{
                                                    emptyText: <div className={'ch-dashboard-warning-text'}>No bookmark
                                                        folders</div>
                                                }}
                                                renderItem={linkGroup => (
                                                    <li>
                                                        <Tooltip
                                                            title={linkGroup.name.length > 30 ? linkGroup.name : ''}>
                                                            <Button
                                                                key={linkGroup.linkGroupId}
                                                                type={'selector'}
                                                                activated={linkGroup.linkGroupId == selectedFolderKey}
                                                                icon={<FolderOutlineIcon aria-hidden={'true'}/>}
                                                                onClick={() => {
                                                                    handleSelectFolder(linkGroup)
                                                                }}>
                                                                {linkGroup.name.length > 30 ? linkGroup.name.slice(0, 30) + '...' : linkGroup.name}
                                                            </Button>
                                                        </Tooltip>
                                                    </li>
                                                )}
                                                selectedkeys={[selectedFolderKey]}
                                            />
                                        </>
                                    }
                                    {mobileScreenMode &&
                                        <div className={'ch-manage-bookmarks-select-wrapper'}>
                                            <div className={'ch-manage-bookmarks-select-label-wrapper'} style={{ flex: '1 1 0', minWidth: 0}}>
                                                <label htmlFor={'bookmark-folder-select'} >
                                                    Bookmark Folder
                                                </label>
                                                <Select
                                                    id={'bookmark-folder-select'}

                                                    placeholder="Select a folder"
                                                    value={selectedFolderKey}
                                                    onChange={(value) => {
                                                        const selected = folders.find(folder => folder.linkGroupId === value);
                                                        if (selected) {
                                                            handleSelectFolder(selected);
                                                        }
                                                    }}
                                                    options={folders.map(folder => ({
                                                        label: (
                                                            <span>
                                                                    {folder.name}
                                                                </span>
                                                        ),
                                                        value: folder.linkGroupId,
                                                        key: folder.linkGroupId,
                                                    }))}
                                                    notFoundContent={<div className="ch-dashboard-warning-text">No bookmark
                                                        folders</div>}
                                                />
                                            </div>
                                            <Button
                                                onClick={() => {
                                                    setAddFolderDialogOpen(true);
                                                }}
                                                aria-label={'Add bookmark folder'}
                                                type={'outline'}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    }
                                </>
                            }
                        </div>
                        <div className={'ch-manage-bookmarks-bookmark-list'}>
                            <div tabIndex={-1} className={'flexAlignCentre flexSpaceBetween padding--m border-b--s ch-bookmark-folder-header'}>
                                <h3>{selectedFolder.name}</h3>
                                <ButtonsGroup>
                                    <Button type={'outline'} ref={renameGroupBtnRef}
                                            aria-label={'Rename bookmark ' + selectedFolder.name}
                                            onClick={() => {
                                                setEditFolderDialogOpen(true);
                                                editFolderForm.setFieldValue('name', selectedFolder.name);
                                            }}>Rename</Button>
                                    <PopConfirm title={<span>{'Delete bookmark folder?'}</span>} placement={'left'}
                                                okText={'Delete'} onConfirm={onArchiveLinkGroup}>
                                        <Button type={'outline'}
                                                aria-label={'Delete bookmark folder ' + selectedFolder.name}>
                                            <Delete/>
                                        </Button>
                                    </PopConfirm>
                                </ButtonsGroup>
                            </div>
                            <CircularLoader visible={isBookmarkLoading} pageCentered={true} displayInElement={true}
                                            spacing={true}/>
                            {!isBookmarkLoading &&
                                <List dataSource={selectedFolder.linkUrls}
                                      locale={{
                                          emptyText: <div className={'ch-dashboard-warning-text'}>No bookmarks in this
                                              folder</div>
                                      }}
                                      renderItem={link => (
                                          <li key={selectedFolder.linkGroupId + '-' + link.linkUrlId}
                                              className={'ch-manage-bookmarks-item'}>
                                              <div>
                                                  <a className={'ch-link'} tabIndex={0} onClick={followBookMarkLink}
                                                     onKeyDown={followBookMarkLink}
                                                     data-concept-id={link.conceptId} data-syllabus-id={link.syllabusId}
                                                     data-url={link.url}
                                                     data-url-hash={link.tabBean !== undefined ? link.urlHash : null}>{link.name}</a>
                                                  <div><span
                                                      className={'ch-text-emphasis'}>Location:</span> {link.conceptName + (link.tabBean !== undefined ? (' → ' + link.tabBean.tabName) : '')}
                                                  </div>
                                              </div>
                                              <ButtonsGroup>
                                                  <Button
                                                      type={'outline'}
                                                      aria-label={'Rename bookmark ' + link.name}
                                                      onClick={() => {
                                                          setEditBookmarkDialogOpen(true);
                                                          editBookmarkForm.setFieldValue('name', link.name);
                                                          setSelectedBookmark(link);
                                                      }}>Rename</Button>
                                                  <PopConfirm title={<span>{'Delete bookmark?'}</span>}
                                                              placement={'left'} okText={'Delete'}
                                                              onConfirm={() => onArchiveLink(link.linkUrlId)}>
                                                      <Button type={'outline'}
                                                              aria-label={'Delete bookmark ' + link.name}>
                                                          <Delete aria-hidden={true} />
                                                      </Button>
                                                  </PopConfirm>
                                              </ButtonsGroup>
                                          </li>
                                      )}
                                />
                            }
                        </div>
                    </>
                }
            </div>
            <GenericDialog
                title={'Add bookmark folder'}
                open={isAddFolderDialogOpen}
                dialogWidth={400}
                onClose={() => {
                    setAddFolderDialogOpen(false);
                    addFolderForm.resetFields();
                }}
                buttons={[
                    {
                        name: 'Close',
                        extraProps: {type: 'text'}
                    },
                    {
                        name: 'Create',
                        callback: onAddLinkGroup
                    }]}>
                <Form form={addFolderForm}
                      labelCol={{span: 1}}
                      wrapperCol={{span: 18}}>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{
                            required: true,
                            message: 'Please input a name.'
                        }]}>
                        <Input placeholder={'Name'}/>
                    </Form.Item>
                </Form>
            </GenericDialog>
            <GenericDialog title={'Edit bookmark folder'} open={isEditFolderDialogOpen}
                           onClose={() => {
                               setEditFolderDialogOpen(false);
                               editFolderForm.resetFields();
                           }}
                           buttons={[{
                               name: 'Close',
                               extraProps: {type: 'text'}
                           },
                               {
                                   name: 'Rename',
                                   callback: onEditLinkGroup
                               }]}>
                <Form form={editFolderForm}
                      labelCol={{span: 1}}
                      wrapperCol={{span: 18}}
                      >
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{
                            required: true,
                            message: 'Please input a name.'
                        }]}>
                        <Input placeholder={'Name'} fullWidth={true}/>
                    </Form.Item>
                </Form>
            </GenericDialog>
            <GenericDialog title={'Edit bookmark'} open={isEditBookmarkDialogOpen}
                           onClose={() => {
                               setEditBookmarkDialogOpen(false);
                               editBookmarkForm.resetFields();
                           }}
                           buttons={[{
                               name: 'Close',
                               extraProps: {type: 'text'}
                           },
                               {
                                   name: 'Rename',
                                   callback: onEditLink
                               }]}>
                <Form form={editBookmarkForm}
                      labelCol={{span: 1}}
                      wrapperCol={{span: 18}}
                      style={{width: 400}}>
                    <Form.Item
                        label="Name"
                        name="name"
                        rules={[{
                            required: true,
                            message: 'Please input a name.'
                        }]}>
                        <Input placeholder={'Name'}/>
                    </Form.Item>
                </Form>
            </GenericDialog>
        </>
    );
}

export default ManageBookmarks;